/**
 * /dashboard — the oddits practice dashboard (canonical).
 * @file src/web/features/main/pages/dashboard.tsx
 *
 * Rendered inside the admin (sidebar) layout — `/dashboard` is mapped to
 * AdminLayoutRoute in App.tsx, so this page returns CONTENT ONLY (the
 * layout owns Header, sidebar and Footer). `/admin` redirects here.
 *
 * Reads GET /api/tally-import/summary (real imported Tally data) and
 * renders client/ledger/voucher totals, a reconciliation panel
 * (debits = credits), recent imports, and a client list. Empty-state
 * prompts the first import.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, ArrowRight, Sparkles } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { PageLoading } from '../../admin/components/PageLoading';
import { adminFetchJson } from '../../admin/lib/admin-api';
import { useCompany } from '../../admin/lib/company-context';
import { PortfolioView, ClientView, type Portfolio, type GapSummary } from './dashboard-views';

interface PracticeSummary {
  totals: { companies: number; ledgers: number; vouchers: number; entries: number };
  reconciliation: { debits: number; credits: number; balanced: boolean };
  recentImports: Array<{
    id: string; companyName: string; kind: string; vouchers: number;
    entries: number; ledgers: number; groups: number; status: string; createdAt: string;
  }>;
  companies: Array<{ id: string; name: string; gstin: string | null; booksFrom: string | null; vouchers: number; ledgers: number }>;
}

export default function DashboardPage() {
  const { entityId, entities, setEntityId } = useCompany();
  const [summary, setSummary] = useState<PracticeSummary | null>(null);
  const [gaps, setGaps] = useState<GapSummary | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Two scopes, two questions, two screens. See dashboard-views.tsx.
  const isPortfolio = !entityId;

  useEffect(() => {
    let cancelled = false;
    setSummary(null); setGaps(null); setPortfolio(null); setError(null);

    if (isPortfolio) {
      adminFetchJson<Portfolio>('/api/masters/portfolio')
        .then((d) => !cancelled && setPortfolio(d))
        .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'));
      return () => { cancelled = true; };
    }

    // Books health and compliance readiness are separate concerns with separate
    // endpoints; fetched together so the screen paints once.
    Promise.all([
      adminFetchJson<PracticeSummary>('/api/tally-import/summary', { query: { entityId } }),
      adminFetchJson<GapSummary>('/api/masters/gaps', { query: { entityId } }).catch(() => null),
    ])
      .then(([s, g]) => { if (cancelled) return; setSummary(s); setGaps(g); })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancelled = true; };
  }, [entityId, isPortfolio]);

  const entityName = entities.find((e) => e.id === entityId)?.name;
  const loading = isPortfolio ? !portfolio : !summary;
  const noBooks = !isPortfolio && summary && summary.totals.companies === 0;

  return (
    <>
      <SEO title="Dashboard" />
      <AdminPageHeader
        title={isPortfolio ? 'Practice overview' : (entityName ?? 'Client overview')}
        breadcrumbs={[{ label: 'oddits' }, { label: 'Dashboard' }]}
      />

      {error && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          Failed to load dashboard: {error}
        </div>
      )}

      {loading && !error && (
        <PageLoading label={isPortfolio ? 'Checking your clients' : 'Loading client'} />
      )}

      {noBooks && <EmptyState />}

      {isPortfolio && portfolio && (
        portfolio.clients.length === 0
          ? <EmptyState />
          : <PortfolioView data={portfolio} onPick={setEntityId} />
      )}

      {!isPortfolio && summary && !noBooks && (
        <ClientView summary={summary} gaps={gaps} />
      )}
    </>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-inkblack text-white">
        <Sparkles className="h-6 w-6" />
      </span>
      <h3 className="mt-5 text-lg font-semibold text-ink-900">No entity data yet</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-600">
        Import from Tally to see your entities, ledgers, vouchers and a live
        reconciliation here — validated before anything lands.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <Link to="/admin/import" className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:-translate-y-0.5">
          <Upload className="h-4 w-4" /> Import from Tally
        </Link>
        <Link to="/user/admin" className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 no-underline transition hover:bg-ink-50">
          Invite your team <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
